// ============================================================================
// todo-client-mod -- 主题变量 & 设计令牌
//
// 统一的设计系统变量，支持浅色/深色主题切换。
// 组件样式通过引用此文件中的令牌保持一致性。
// ============================================================================
/* -------------------------------------------------------------------------- */
/* 色系                                                                       */
/* -------------------------------------------------------------------------- */
/** 主色板 */
const PALETTE = {
    /** Indigo — 主品牌色 */
    primary: {
        50: '#eef2ff',
        100: '#e0e7ff',
        200: '#c7d2fe',
        300: '#a5b4fc',
        400: '#818cf8',
        500: '#6366f1',
        600: '#4f46e5',
        700: '#4338ca',
        800: '#3730a3',
        900: '#312e81',
    },
    /** Emerald — 成功/完成 */
    success: {
        50: '#ecfdf5',
        100: '#d1fae5',
        300: '#6ee7b7',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
    },
    /** Amber — 警告/中等优先级 */
    warning: {
        50: '#fffbeb',
        100: '#fef3c7',
        300: '#fcd34d',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
    },
    /** Rose — 危险/删除/高优先级 */
    danger: {
        50: '#fff1f2',
        100: '#ffe4e6',
        300: '#fda4af',
        500: '#f43f5e',
        600: '#e11d48',
        700: '#be123c',
    },
    /** Orange — 紧急优先级 */
    urgent: {
        50: '#fff7ed',
        100: '#ffedd5',
        300: '#fdba74',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
    },
    /** Slate — 文本和边框 */
    slate: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
        950: '#020617',
    },
};
export const lightTheme = {
    bg: {
        primary: '#f9fafb',
        secondary: '#ffffff',
        tertiary: '#f3f4f6',
        elevated: '#ffffff',
        overlay: 'rgba(0, 0, 0, 0.4)',
    },
    text: {
        primary: '#0f172a',
        secondary: '#475569',
        tertiary: '#94a3b8',
        disabled: '#cbd5e1',
        inverse: '#ffffff',
    },
    border: {
        default: '#e5e7eb',
        hover: '#d1d5db',
        focus: '#6366f1',
        divider: '#f3f4f6',
    },
    interaction: {
        hover: '#f3f4f6',
        active: '#e5e7eb',
        selected: '#eef2ff',
    },
    status: {
        primary: PALETTE.primary[500],
        primaryHover: PALETTE.primary[600],
        success: PALETTE.success[500],
        successBg: PALETTE.success[50],
        warning: PALETTE.warning[500],
        warningBg: PALETTE.warning[50],
        danger: PALETTE.danger[500],
        dangerBg: PALETTE.danger[50],
    },
    shadow: {
        sm: '0 1px 2px rgba(0,0,0,0.04)',
        md: '0 4px 12px rgba(0,0,0,0.06)',
        lg: '0 8px 24px rgba(0,0,0,0.08)',
        xl: '0 16px 48px rgba(0,0,0,0.12)',
    },
};
export const darkTheme = {
    bg: {
        primary: '#0f172a',
        secondary: '#1e293b',
        tertiary: '#334155',
        elevated: '#1e293b',
        overlay: 'rgba(0, 0, 0, 0.6)',
    },
    text: {
        primary: '#f1f5f9',
        secondary: '#94a3b8',
        tertiary: '#64748b',
        disabled: '#475569',
        inverse: '#1e293b',
    },
    border: {
        default: '#334155',
        hover: '#475569',
        focus: '#818cf8',
        divider: '#1e293b',
    },
    interaction: {
        hover: '#334155',
        active: '#475569',
        selected: '#312e81',
    },
    status: {
        primary: PALETTE.primary[400],
        primaryHover: PALETTE.primary[300],
        success: PALETTE.success[300],
        successBg: '#064e3b',
        warning: PALETTE.warning[300],
        warningBg: '#78350f',
        danger: PALETTE.danger[300],
        dangerBg: '#881337',
    },
    shadow: {
        sm: '0 1px 2px rgba(0,0,0,0.2)',
        md: '0 4px 12px rgba(0,0,0,0.3)',
        lg: '0 8px 24px rgba(0,0,0,0.4)',
        xl: '0 16px 48px rgba(0,0,0,0.5)',
    },
};
/* -------------------------------------------------------------------------- */
/* 排版                                                                       */
/* -------------------------------------------------------------------------- */
export const typography = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
        xs: '11px',
        sm: '12px',
        base: '14px',
        md: '15px',
        lg: '16px',
        xl: '18px',
        '2xl': '24px',
        '3xl': '28px',
    },
    fontWeight: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeight: {
        tight: '1.25',
        normal: '1.5',
        relaxed: '1.7',
    },
};
/* -------------------------------------------------------------------------- */
/* 间距                                                                       */
/* -------------------------------------------------------------------------- */
export const spacing = {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
};
/* -------------------------------------------------------------------------- */
/* 圆角                                                                       */
/* -------------------------------------------------------------------------- */
export const radius = {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
};
/* -------------------------------------------------------------------------- */
/* 过渡                                                                       */
/* -------------------------------------------------------------------------- */
export const transition = {
    fast: '0.12s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
    spring: '0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    fade: 'opacity 0.2s ease',
    slide: 'transform 0.2s ease, opacity 0.2s ease',
};
/* -------------------------------------------------------------------------- */
/* 断点                                                                       */
/* -------------------------------------------------------------------------- */
export const breakpoints = {
    sm: 480,
    md: 768,
    lg: 1024,
    xl: 1280,
};
/* -------------------------------------------------------------------------- */
/* 优先级颜色映射                                                              */
/* -------------------------------------------------------------------------- */
export const priorityColors = {
    low: PALETTE.slate[400],
    medium: PALETTE.warning[500],
    high: PALETTE.danger[500],
    urgent: PALETTE.urgent[500],
};
export const priorityBgColors = {
    low: PALETTE.slate[100],
    medium: PALETTE.warning[50],
    high: PALETTE.danger[50],
    urgent: PALETTE.urgent[50],
};
/* -------------------------------------------------------------------------- */
/* 状态颜色映射                                                               */
/* -------------------------------------------------------------------------- */
export const statusColors = {
    pending: { bg: PALETTE.slate[100], text: PALETTE.slate[600], border: PALETTE.slate[300] },
    in_progress: { bg: '#dbeafe', text: '#2563eb' },
    completed: { bg: '#d1fae5', text: '#059669' },
    cancelled: { bg: PALETTE.slate[100], text: PALETTE.slate[400] },
    draft: { bg: PALETTE.slate[100], text: PALETTE.slate[600], border: PALETTE.slate[300] },
    published: { bg: '#d1fae5', text: '#059669' },
    archived: { bg: PALETTE.slate[100], text: PALETTE.slate[400] },
};
/* -------------------------------------------------------------------------- */
/* 工具函数                                                                    */
/* -------------------------------------------------------------------------- */
/** 获取优先级图标颜色 */
export function getPriorityColor(priority) {
    return priorityColors[priority] ?? PALETTE.slate[400];
}
/** 获取状态颜色配置 */
export function getStatusColors(status) {
    return statusColors[status] ?? { bg: PALETTE.slate[100], text: PALETTE.slate[600] };
}
//# sourceMappingURL=theme.js.map